import Icon, { FileExcelOutlined } from '@ant-design/icons';
import { Button, Dropdown, Menu, message, Modal, Space, Tag, Tooltip } from 'antd';
import Search from 'antd/lib/input/Search';
import { ColumnsType, TablePaginationConfig } from 'antd/lib/table';
import { Key, TableRowSelection } from 'antd/lib/table/interface';
import React, { Component } from 'react';
import { FormattedDate, FormattedMessage, injectIntl, WrappedComponentProps } from 'react-intl';
import { Link } from 'react-router-dom';
import companyApi from '../../../api/CompanyApi';
import hubApi from '../../../api/HubApi';
import memberApi from '../../../api/MemberApi';
import LayoutComponent from '../../../components/LayoutComponent/LayoutComponent';
import SelectTableComponent from '../../../components/SelectTableComponent/SelectTableComponent';
import CustomContext from '../../../context/CustomContext';
import { Page } from '../../../model/elements';
import { Company, Member } from '../../../model/entities';
import { Bim360Status, MemberType } from '../../../model/types';
import { ReactComponent as CaretDownSvg } from '../../../resources/images/caret-down.svg';
import { ReactComponent as CheckmarkSvg } from '../../../resources/images/checkmark.svg';
import { ReactComponent as CloudUploadSvg } from '../../../resources/images/cloud-upload.svg';
import { ReactComponent as EditSvg } from '../../../resources/images/edit.svg';
import { ReactComponent as InformationSvg } from '../../../resources/images/information.svg';
import { ReactComponent as TrashCanSvg } from '../../../resources/images/trash-can.svg';
import { ReactComponent as WarningSvg } from '../../../resources/images/warning.svg';
import notificationService from '../../../services/NotificationService';
import tableService from '../../../services/TableService';
import UpdateCompanyModal from '../ProjectMembersPage/UpdateCompanyModal/UpdateCompanyModal';
import UpdateCostCenterModal from '../ProjectMembersPage/UpdateCostCenterModal/UpdateCostCenterModal';

class MembersPage extends Component<Props, State> {
    static contextType = CustomContext;
    context!: React.ContextType<typeof CustomContext>;

    constructor(props: Props) {
        super(props);
        this.state = {
            selectedRowKeys: [],
            selectedMembers: [],
            companies: [],
        };
    }

    componentDidMount() {
        this.init();
    }

    /** METHODS **/

    init = async () => {
        try {
            this.setState({ loading: true });
            const hubs = await hubApi.list(true);
            const responses = await Promise.all([
                memberApi.list(0, tableService.pageSize, 'lastName', true),
                companyApi.list(hubs[0].id),
            ]);
            const membersPage = responses[0];
            const companies = responses[1];

            this.setState({ membersPage, companies });
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: false });
        }
    };

    list = async () => {
        const { searchText, membersPage } = this.state;
        try {
            this.setState({ loading: true });
            const page = 0;
            const size = membersPage!.size;
            const sortField = membersPage!.sort.field!;
            const sortOrder = membersPage!.sort.order!;
            await this.listMembers(page, size, sortField, sortOrder, searchText);
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: false });
        }
    };

    search = async (searchText: string) => {
        const { membersPage } = this.state;
        try {
            this.setState({ loading: true, searchText });
            const page = 0;
            const size = membersPage!.size;
            const sortField = membersPage!.sort.field!;
            const sortOrder = membersPage!.sort.order!;
            await this.listMembers(page, size, sortField, sortOrder, searchText);
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: false });
        }
    };

    paginate = async (pagination: TablePaginationConfig, filters: any, sorter: any) => {
        const { searchText } = this.state;
        try {
            this.setState({ loading: true });
            const page = pagination.current! - 1;
            const size = pagination.pageSize!;
            const sortField = sorter.field;
            const sortOrder = sorter.order === 'ascend';
            await this.listMembers(page, size, sortField, sortOrder, searchText);
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: false });
        }
    };

    listMembers = async (page: number, size: number, sortField: string, sortOrder: boolean, searchText?: string) => {
        const membersPage = await memberApi.list(page, size, sortField, sortOrder, searchText);
        this.setState({ membersPage });
    };

    exportSpreadSheet = async () => {
        const { membersPage, searchText } = this.state;
        try {
            this.setState({ exporting: true });
            const sortField = membersPage!.sort.field!;
            const sortOrder = membersPage!.sort.order!;
            await memberApi.exportSpreadSheet(sortField, sortOrder, searchText);
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ exporting: false });
        }
    };

    showInactivateModal = () => {
        const { intl } = this.props;
        Modal.confirm({
            title: intl.formatMessage({ id: 'members.inactivate.modal.title' }),
            icon: <Icon component={WarningSvg} />,
            okText: intl.formatMessage({ id: 'button.yes' }),
            okType: 'danger',
            cancelText: intl.formatMessage({ id: 'button.no' }),
            onOk: () => this.inactivate(),
        });
    };

    showActivateModal = () => {
        const { intl } = this.props;
        Modal.confirm({
            title: intl.formatMessage({ id: 'members.activate.modal.title' }),
            icon: <Icon component={InformationSvg} />,
            okText: intl.formatMessage({ id: 'button.yes' }),
            okType: 'primary',
            cancelText: intl.formatMessage({ id: 'button.no' }),
            onOk: () => this.activate(),
        });
    };

    showCompanyModal = (visible: boolean) => {
        const companyModalVisible = visible;
        this.setState({ companyModalVisible });
    };

    showCostCenterModal = (visible: boolean) => {
        const costCenterModalVisible = visible;
        this.setState({ costCenterModalVisible });
    };

    activate = async () => {
        this.updateStatus(true);
    };

    inactivate = async () => {
        this.updateStatus(false);
    };

    updateCompany = async (values: any) => {
        const { selectedMembers, companies } = this.state;
        try {
            let selectedCompany = companies.find((c) => c.id === values.company);
            const members = [...selectedMembers];
            members.forEach((c) => (c.company = selectedCompany));
            const membersWithStatus = await memberApi.updateCompany(members);
            this.unselectAll();
            await this.list();
            this.showCompanyModal(false);

            // display message
            if (membersWithStatus.every((m) => m.status.type === 'OK')) {
                message.success(this.props.intl.formatMessage({ id: 'status.saved' }));
            } else {
                message.warning(this.props.intl.formatMessage({ id: 'status.savedWithErrors' }), 5);
            }
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        }
    };

    updateCostCenter = async (values: any) => {
        const { selectedMembers } = this.state;
        try {
            // update cost center
            const members = [...selectedMembers];
            members.forEach((c) => (c.costCenter = values.costCenter));
            const membersWithStatus = await memberApi.updateCostCenter(members);
            this.unselectAll();
            await this.list();
            this.showCostCenterModal(false);

            // display message
            if (membersWithStatus.every((m) => m.status.type === 'OK')) {
                message.success(this.props.intl.formatMessage({ id: 'status.saved' }));
            } else {
                message.warning(this.props.intl.formatMessage({ id: 'status.savedWithErrors' }), 5);
            }
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: false });
        }
    };

    updateStatus = async (checked: boolean) => {
        const { selectedMembers } = this.state;

        try {
            this.setState({ loading: true });
            const members = selectedMembers.map((m) => {
                m.bim360Status = checked ? 'active' : 'inactive';
                return m;
            });
            const membersWithStatus = await memberApi.updateStatus(members);
            this.unselectAll();
            await this.list();

            // display message
            if (membersWithStatus.every((m) => m.status.type === 'OK')) {
                checked
                    ? message.success(this.props.intl.formatMessage({ id: 'members.activate.status' }))
                    : message.success(this.props.intl.formatMessage({ id: 'members.inactivate.status' }));
            } else {
                message.warning(this.props.intl.formatMessage({ id: 'status.savedWithErrors' }), 5);
            }
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: false });
        }
    };

    select = (selectedRowKeys: Key[], selectedRows: Member[]) => {
        this.setState({ selectedRowKeys, selectedMembers: selectedRows });
    };

    unselectAll = () => {
        this.setState({ selectedRowKeys: [], selectedMembers: [] });
    };

    isUpdateCompaniesButtonDisabled = (): boolean => {
        const { selectedMembers } = this.state;
        return selectedMembers.length === 0;
    };

    isUpdateCostCenterButtonDisabled = (): boolean => {
        const { selectedMembers } = this.state;
        return selectedMembers.length === 0;
    };

    isInactiveButtonDisabled = (): boolean => {
        const { selectedMembers } = this.state;
        return (
            selectedMembers.length === 0 ||
            selectedMembers.filter((m) => m.bim360Status !== 'active').length > 0 ||
            selectedMembers.filter((m) => m.email === this.context.auth!.email).length > 0
        );
    };

    isActiveButtonDisabled = (): boolean => {
        const { selectedMembers } = this.state;
        return selectedMembers.length === 0 || selectedMembers.filter((m) => m.bim360Status !== 'inactive').length > 0;
    };

    /*** COMPONENTS ***/

    renderContent = (): React.ReactElement | undefined => {
        return (
            <>
                {this.renderToolbar()}
                {this.renderTable()}
                {this.renderCompanyModal()}
                {this.renderCostCenterModal()}
            </>
        );
    };

    renderToolbar = (): React.ReactElement | undefined => {
        const { intl } = this.props;
        const { exporting } = this.state;
        const placeholder: string = intl.formatMessage({ id: 'members.search' });

        return (
            <div className="toolbar">
                <Search placeholder={placeholder} onSearch={this.search} size="large" className="search" />
                <Space>
                    <Tooltip title={<FormattedMessage id="members.exportSpreadSheet" />}>
                        <Button
                            size="large"
                            icon={<FileExcelOutlined />}
                            onClick={this.exportSpreadSheet}
                            loading={exporting}
                        ></Button>
                    </Tooltip>
                    <Link to="/setup/members/import">
                        <Tooltip title={<FormattedMessage id="members.import" />}>
                            <Button size="large" icon={<Icon component={CloudUploadSvg} />}></Button>
                        </Tooltip>
                    </Link>
                    <Dropdown
                        trigger={['click']}
                        placement="bottomRight"
                        overlay={
                            <Menu>
                                <Menu.Item
                                    key="company"
                                    icon={<Icon component={EditSvg} />}
                                    onClick={() => this.showCompanyModal(true)}
                                    disabled={this.isUpdateCompaniesButtonDisabled()}
                                >
                                    <FormattedMessage id="projectMembers.updateCompany" />
                                </Menu.Item>
                                <Menu.Item
                                    key="costCenter"
                                    icon={<Icon component={EditSvg} />}
                                    onClick={() => this.showCostCenterModal(true)}
                                    disabled={this.isUpdateCostCenterButtonDisabled()}
                                    hidden={!this.context.auth?.authorities.includes('ROLE_ADMIN')}
                                >
                                    <FormattedMessage id="projectMembers.updateCostCenter" />
                                </Menu.Item>
                                <Menu.Divider />
                                <Menu.Item
                                    key="activate"
                                    icon={<Icon component={CheckmarkSvg} />}
                                    onClick={this.showActivateModal}
                                    disabled={this.isActiveButtonDisabled()}
                                >
                                    <FormattedMessage id="members.activate" />
                                </Menu.Item>
                                <Menu.Item
                                    key="inactivate"
                                    icon={<Icon component={TrashCanSvg} />}
                                    onClick={this.showInactivateModal}
                                    disabled={this.isInactiveButtonDisabled()}
                                >
                                    <FormattedMessage id="members.inactivate" />
                                </Menu.Item>
                            </Menu>
                        }
                    >
                        <Button type="primary" size="large">
                            <FormattedMessage id="button.actions" /> <Icon component={CaretDownSvg} />
                        </Button>
                    </Dropdown>
                </Space>
            </div>
        );
    };

    renderCompanyModal = (): React.ReactElement | undefined => {
        const { companyModalVisible, companies } = this.state;

        return (
            <UpdateCompanyModal
                companies={companies}
                visible={companyModalVisible}
                onUpdate={this.updateCompany}
                onCancel={() => this.showCompanyModal(false)}
                mandatory={true}
            />
        );
    };

    renderCostCenterModal = (): React.ReactElement | undefined => {
        const { costCenterModalVisible } = this.state;

        return (
            <UpdateCostCenterModal
                visible={costCenterModalVisible}
                onUpdate={this.updateCostCenter}
                onCancel={() => this.showCostCenterModal(false)}
            />
        );
    };

    renderTable = (): React.ReactElement | undefined => {
        const { membersPage, loading, selectedMembers, selectedRowKeys } = this.state;
        const items = membersPage ? membersPage.content : [];
        const columns: ColumnsType<Member> = [
            {
                title: <FormattedMessage id="member.lastName" />,
                dataIndex: 'lastName',
                key: 'lastName',
                sorter: true,
                defaultSortOrder: 'ascend',
            },
            {
                title: <FormattedMessage id="member.firstName" />,
                dataIndex: 'firstName',
                key: 'firstName',
                sorter: true,
            },
            {
                title: <FormattedMessage id="member.email" />,
                dataIndex: 'email',
                key: 'email',
                sorter: true,
            },
            {
                title: <FormattedMessage id="member.company" />,
                dataIndex: 'company',
                key: 'company',
                sorter: true,
                render: (value: Company) => value?.name,
            },
            {
                title: <FormattedMessage id="member.workDocs" />,
                dataIndex: 'workDocs',
                key: 'workDocs',
                sorter: true,
                width: 90,
                align: 'center',
            },
            {
                title: <FormattedMessage id="member.bim360" />,
                dataIndex: 'bim360',
                key: 'bim360',
                sorter: true,
                width: 105,
                align: 'center',
                render: (value: MemberType) => {
                    return <FormattedMessage id={value} />;
                },
            },
            {
                title: <FormattedMessage id="member.bim360CreatedAt" />,
                dataIndex: 'bim360CreatedAt',
                key: 'bim360CreatedAt',
                sorter: true,
                width: 120,
                align: 'center',
                render: (value: MemberType) => {
                    return <FormattedDate value={value} day="2-digit" month="2-digit" year="numeric" />;
                },
            },
            {
                title: <FormattedMessage id="member.bim360LastSignIn" />,
                dataIndex: 'bim360LastSignIn',
                key: 'bim360LastSignIn',
                sorter: true,
                width: 160,
                align: 'center',
                render: (value: MemberType) => {
                    return value == null ? null : (
                        <FormattedDate
                            value={value}
                            day="2-digit"
                            month="2-digit"
                            year="numeric"
                            hour="2-digit"
                            minute="2-digit"
                        />
                    );
                },
            },
            {
                title: <FormattedMessage id="member.bim360Status" />,
                dataIndex: 'bim360Status',
                key: 'bim360Status',
                sorter: true,
                width: 120,
                align: 'center',
                render: (value: Bim360Status) => {
                    const color = value === 'active' ? 'blue' : value === 'inactive' ? 'red' : 'orange';
                    return (
                        <Tag color={color}>
                            <FormattedMessage id={value} />
                        </Tag>
                    );
                },
            },
            {
                title: <FormattedMessage id="member.costCenter" />,
                dataIndex: 'costCenter',
                key: 'costCenter',
                align: 'center',
                sorter: true,
                width: 130,
            },
        ];
        const rowSelection: TableRowSelection<Member> = {
            preserveSelectedRowKeys: true,
            selectedRowKeys,
            onChange: this.select,
        };

        return (
            <SelectTableComponent
                dataSource={items}
                columns={columns}
                pagination={tableService.createPagination(membersPage)}
                rowKey="email"
                onChange={this.paginate}
                sortDirections={['ascend', 'descend']}
                showSorterTooltip={false}
                loading={loading}
                rowSelection={rowSelection}
                selectedItems={selectedMembers.map((member) => member.email)}
                onUnselectAll={this.unselectAll}
            />
        );
    };

    render() {
        return <LayoutComponent pageId="members">{this.renderContent()}</LayoutComponent>;
    }
}
export default injectIntl(MembersPage);

interface Props extends WrappedComponentProps {}

interface State {
    membersPage?: Page<Member>;
    searchText?: string;
    loading?: boolean;
    exporting?: boolean;
    selectedRowKeys: Key[];
    selectedMembers: Member[];
    companyModalVisible?: boolean;
    costCenterModalVisible?: boolean;
    companies: Company[];
}
